import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRESS_STEPS = [
  "Planning query…",
  "Fetching data…",
  "Writing narrative…",
  "Validating citations…",
];

export default function ReportNew() {
  const nav = useNavigate();
  const [question, setQuestion] = useState("");
  const [contextCcn, setContextCcn] = useState("");
  const [contextEin, setContextEin] = useState("");
  const [generating, setGenerating] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState(null);

  const generate = async (e) => {
    e?.preventDefault();
    if (!question.trim()) return;
    setError(null);
    setGenerating(true);
    setStepIdx(0);
    // Advance progress messages while the request is in-flight (LLM call is ~30s).
    const ticker = setInterval(() => {
      setStepIdx((idx) => Math.min(idx + 1, PROGRESS_STEPS.length - 1));
    }, 8000);
    try {
      const r = await axios.post(
        `${API}/reports/ask`,
        {
          question: question.trim(),
          context_ccn: contextCcn.trim() || undefined,
          context_ein: contextEin.trim() || undefined,
        },
        { withCredentials: true, timeout: 240000 },
      );
      clearInterval(ticker);
      const id = r.data?.report_id;
      if (id) nav(`/reports/${id}`);
      else setError("No report_id returned");
    } catch (e) {
      clearInterval(ticker);
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="report-new-form">
      <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">LLM Report Builder</div>
      <h1 className="text-xl font-semibold tracking-tight text-zinc-900 mb-4">New report</h1>

      <form onSubmit={generate} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase font-mono text-zinc-500 mb-1">Question</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. What is Parkland's operating margin versus its peer cohort?"
            rows={4}
            className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm font-mono outline-none focus:border-emerald-600"
            data-testid="report-question-input"
            disabled={generating}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-mono text-zinc-500 mb-1">Context CCN (optional)</label>
            <input
              value={contextCcn}
              onChange={(e) => setContextCcn(e.target.value)}
              placeholder="450015"
              className="w-full border border-zinc-300 rounded-md px-3 py-1.5 text-sm font-mono"
              data-testid="report-context-ccn"
              disabled={generating}
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-mono text-zinc-500 mb-1">Context EIN (optional)</label>
            <input
              value={contextEin}
              onChange={(e) => setContextEin(e.target.value)}
              placeholder="System EIN"
              className="w-full border border-zinc-300 rounded-md px-3 py-1.5 text-sm font-mono"
              data-testid="report-context-ein"
              disabled={generating}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit"
                  disabled={generating || !question.trim()}
                  className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-sm font-mono uppercase tracking-wider"
                  data-testid="report-generate-btn">
            {generating ? "Generating…" : "Generate report"}
          </button>
          {generating && (
            <div className="text-xs font-mono text-zinc-600 flex items-center gap-2" data-testid="report-progress">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              {PROGRESS_STEPS[stepIdx]}
            </div>
          )}
        </div>
        {error && <div className="text-sm text-red-700 font-mono" data-testid="report-error">{error}</div>}
      </form>

      <div className="mt-8 text-[11px] font-mono text-zinc-500">
        <div className="uppercase tracking-wider mb-1">Tips</div>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Be specific. Reference a hospital by name or CCN, a system by EIN.</li>
          <li>Examples: peer cohort comparison, financial trend, composite quality grade explanation.</li>
          <li>If data is unavailable, the narrative will say so — it will not invent numbers.</li>
        </ul>
      </div>
    </div>
  );
}
